import { useEffect, useRef } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Loader2 } from 'lucide-react';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { BlogPostItem } from '@/components/BlogPostItem';
import { Skeleton } from '@/components/ui/skeleton';
import { useShowcasePosts } from '@/hooks/useShowcasePosts';
import { useAuthor } from '@/hooks/useAuthor';
import { getDisplayName } from '@/lib/getDisplayName';
import { getShowcasePubkey } from '@/lib/showcase';

function PostSkeleton() {
  return (
    <div className="flex flex-col">
      <Skeleton className="aspect-[16/10] w-full rounded-xl" />
      <Skeleton className="mt-4 h-3 w-16" />
      <Skeleton className="mt-3 h-6 w-11/12" />
      <Skeleton className="mt-3 h-4 w-full" />
      <Skeleton className="mt-1.5 h-4 w-4/5" />
      <Skeleton className="mt-4 h-3 w-40" />
    </div>
  );
}

const Index = () => {
  const pubkey = getShowcasePubkey();
  const author = useAuthor(pubkey);
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useShowcasePosts(pubkey);

  const metadata = author.data?.metadata;
  const displayName = metadata?.display_name || getDisplayName(metadata, pubkey);

  useSeoMeta({
    title: metadata?.name ? displayName : 'Nostr Showcase',
    description:
      metadata?.about?.slice(0, 160) ||
      `Posts from ${displayName} on the Nostr network.`,
    ogTitle: metadata?.name ? displayName : 'Nostr Showcase',
    ogDescription:
      metadata?.about?.slice(0, 160) ||
      `Posts from ${displayName} on the Nostr network.`,
    ogImage: metadata?.banner || metadata?.picture,
  });

  const posts = data?.pages.flat() ?? [];

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '800px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main className="mx-auto max-w-5xl px-4 py-12 sm:py-16">
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {Array.from({ length: 6 }).map((_, i) => (
              <PostSkeleton key={i} />
            ))}
          </div>
        )}

        {isError && (
          <div className="rounded-xl border border-dashed border-border py-16 px-8 text-center">
            <p className="mx-auto max-w-sm text-muted-foreground">
              Something went wrong loading posts. Try refreshing, or check the
              relay connections.
            </p>
          </div>
        )}

        {!isLoading && !isError && posts.length === 0 && (
          <div className="rounded-xl border border-dashed border-border py-16 px-8 text-center">
            <p className="mx-auto max-w-sm text-muted-foreground">
              No posts found yet. This account may not have published any notes,
              or they haven't reached the connected relays.
            </p>
          </div>
        )}

        {posts.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {posts.map((event) => (
              <BlogPostItem key={event.id} event={event} />
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel + loader */}
        <div ref={sentinelRef} className="h-px" />
        {isFetchingNextPage && (
          <div className="flex justify-center py-10">
            <Loader2 className="size-6 animate-spin text-primary" />
          </div>
        )}
        {!hasNextPage && posts.length > 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">
            You've reached the end.
          </p>
        )}
      </main>

      <SiteFooter />
    </div>
  );
};

export default Index;
